import { Autocomplete, Box, Checkbox, CircularProgress, Divider, Grid, IconButton, InputAdornment, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import theme from "../../theme";
import { Add, BorderColor, Close, DeleteForever, Search, VisibilityOutlined } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import CustomTimePicker from "../../common/components/CustomTimePicker";
import moment from "moment";
import { createAlert, deleteAlert, getAlertList, updateAlert } from "../../api/adminAlertApi";
import { ReceipientTypes, removeEmptyFields, TasksPriorityArr } from "../../common/const";
import CommonSelect from "../../common/components/CommonSelect";
import { getUserList } from "../../api/authApi";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto',
    maxHeight: '400px',
    overflow: 'auto',
}));

// ---------- Validation helpers ----------
const HAS_LETTER_REGEX = /[A-Za-z]/;
const NUMERIC_ONLY_REGEX = /^\d+$/;

const validateField = (name, value, allValues = {}) => {
    switch (name) {
        case 'title': {
            if (!value || !value.trim()) return 'Title is required';
            if (NUMERIC_ONLY_REGEX.test(value.trim())) return 'Title cannot contain only numbers';
            if (!HAS_LETTER_REGEX.test(value)) return 'Please enter a valid title';
            if (value.trim().length < 3) return 'Title must be at least 3 characters';
            return '';
        }
        case 'message': {
            if (!value || !value.trim()) return 'Message is required';
            if (NUMERIC_ONLY_REGEX.test(value.trim())) return 'Message cannot contain only numbers';
            if (!HAS_LETTER_REGEX.test(value)) return 'Please enter a valid message';
            return '';
        }
        case 'start_date': {
            if (!value) return 'Start Date is required';
            return '';
        }
        case 'start_time': {
            if (!value) return 'Start Time is required';
            return '';
        }
        case 'expiry_date': {
            // expiry is optional per existing code (empty string allowed in save payload),
            // but if user enters expiry_time, expiry_date must also be present, and vice versa.
            if (!value && allValues?.expiry_time) return 'Expiry Date is required when Expiry Time is set';
            if (value && allValues?.start_date && allValues?.start_time && allValues?.expiry_time) {
                const start = new Date(`${allValues.start_date}T${allValues.start_time}:00Z`);
                const expiry = new Date(`${value}T${allValues.expiry_time}:00Z`);
                if (expiry <= start) return 'Expiry must be after Start';
            }
            return '';
        }
        case 'expiry_time': {
            if (!value && allValues?.expiry_date) return 'Expiry Time is required when Expiry Date is set';
            return '';
        }
        default:
            return '';
    }
};

const ManageAlert = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [addOpen, setAddOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [meetings, setMeetings] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editingMeetings, setEditingMeetings] = useState(null);
    const [clearSearch, setClearSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [users, setUsers] = useState([]);
    const [userPageNo, setUserPageNo] = useState(1);
    const [isAvailable, setIsAvailable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const rowsPerPage = 10;
    const [newMeeting, setNewMeeting] = useState({
        title: '',
        message: '',
        receipientType: ReceipientTypes[0]?.value,
        priority: TasksPriorityArr[0]?.value,
        studentIds: [],
        start_date: '',
        start_time: '',
        expiry_date: '',
        expiry_time: '',
    });

    const getAlerts = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getAlertList(pageNo + 1, pageSize, searchText);
            setMeetings(response.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        getAlerts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNo, pageSize, clearSearch]);

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleDelete = async (id) => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await deleteAlert(id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({ loading: false }));
                setDeleteConfirm(null);
                getAlerts();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleOpenDialog = async (blog = null) => {
        setEditingMeetings(blog);
        setErrors({});
        dispatch(setLoading({ loading: true }));
        setNewMeeting({
            title: blog.title,
            message: blog.message,
            start_date: blog?.start_at ? moment.utc(blog?.start_at).format('YYYY-MM-DD') : '',
            start_time: blog?.start_at ? moment.utc(blog?.start_at).format('HH:mm') : '',
            expiry_date: blog?.expiry_at ? moment.utc(blog?.expiry_at).format('YYYY-MM-DD') : '',
            expiry_time: blog?.expiry_at ? moment.utc(blog?.expiry_at).format('HH:mm') : '',
            receipientType: blog?.recipient_type,
            priority: blog?.priority,
            studentIds: blog?.alertRecipients?.map((a) => a?.student?.student_id) || []
        });
        dispatch(setLoading({ loading: false }));
        setAddOpen(true);
    };

    const handleCloseDialog = () => {
        setEditingMeetings(null);
        setViewOpen(false);
        setErrors({});
        setNewMeeting({
            title: '',
            message: '',
            start_date: '',
            start_time: '',
            expiry_date: '',
            expiry_time: '',
            receipientType: ReceipientTypes[0]?.value,
            priority: TasksPriorityArr[0]?.value,
            studentIds: [],
        });
        setAddOpen(false);
    };

    // Unified change handler that updates state AND validates
    const handleFieldChange = (name, value) => {
        setNewMeeting((prev) => {
            const next = { ...prev, [name]: value };
            // Re-validate the changed field plus any dependent date/time fields
            setErrors((prevErrors) => {
                const updated = { ...prevErrors, [name]: validateField(name, value, next) };
                if (['start_date', 'start_time', 'expiry_date', 'expiry_time'].includes(name)) {
                    updated.expiry_date = validateField('expiry_date', next.expiry_date, next);
                    updated.expiry_time = validateField('expiry_time', next.expiry_time, next);
                }
                return updated;
            });
            return next;
        });
    };

    const validateAll = () => {
        const fieldsToValidate = ['title', 'message', 'start_date', 'start_time', 'expiry_date', 'expiry_time'];
        const newErrors = {};
        fieldsToValidate.forEach((field) => {
            const err = validateField(field, newMeeting[field], newMeeting);
            if (err) newErrors[field] = err;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveBlog = async () => {
        if (!validateAll()) {
            showNotification('Please fix the errors before submitting', 'error');
            return;
        }
        try {
            dispatch(setLoading({ loading: true }));
            let response;
            let data = {
                title: newMeeting?.title,
                message: newMeeting?.message,
                start_at: new Date(`${newMeeting.start_date}T${newMeeting?.start_time}:00Z`).toISOString(),
                expiry_at: newMeeting.expiry_date && newMeeting.expiry_time
                    ? new Date(`${newMeeting.expiry_date}T${newMeeting?.expiry_time}:00Z`).toISOString()
                    : '',
                recipient_type: newMeeting?.receipientType,
                priority: newMeeting?.priority
            };
            if (newMeeting?.studentIds?.length > 0) {
                data.student_ids = newMeeting?.studentIds;
            }
            if (editingMeetings) {
                response = await updateAlert(editingMeetings.alert_id, removeEmptyFields(data));
            } else {
                response = await createAlert(removeEmptyFields(data));
            }
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({ loading: false }));
                getAlerts();
            }
            handleCloseDialog();
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const getUserData = async () => {
        try {
            setIsLoading(true);
            const response = await getUserList(userPageNo, rowsPerPage, '');
            const { data } = response?.data;
            if (data?.length > 0) {
                setIsAvailable(true);
                const array = [...users, ...data];
                const uniqueArray = [
                    ...new Set(array.map((item) => JSON.stringify(item))),
                ].map((item) => JSON.parse(item));
                setUsers(uniqueArray);
                setIsLoading(false);
            } else {
                setIsAvailable(false);
                setIsLoading(false);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (newMeeting?.receipientType !== 'ALL') {
            getUserData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newMeeting?.receipientType, pageNo]);

    const loadNextUser = async (event) => {
        const listBoxNode = event.currentTarget;
        const numberOfPages = userPageNo + 1;
        const position = listBoxNode.scrollTop + listBoxNode.clientHeight;
        if (listBoxNode.scrollHeight - position <= 1 && isAvailable) {
            setUserPageNo(numberOfPages);
            await getUserData();
        }
    };

    const hasErrors = Object.values(errors).some((e) => !!e);
    const isFormIncomplete =
        !newMeeting.title?.trim() ||
        !newMeeting.message?.trim() ||
        !newMeeting.start_date ||
        !newMeeting.start_time;

    return (
        <Fragment>
            {loading && (<LoadingSpinner />)}
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to permanently delete this alert?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => { handleDelete(deleteConfirm.alert_id); }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => { setDeleteConfirm(null); }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={addOpen}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                {viewOpen ? 'View Alert' : editingMeetings ? 'Edit Alert' : 'Add New Alert'}
                            </Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => { handleCloseDialog(); }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Title *"
                                name="title"
                                value={newMeeting.title}
                                onChange={(e) => handleFieldChange('title', e.target.value)}
                                placeholder="Enter the title"
                                disabled={viewOpen}
                                error={!!errors.title}
                                helperText={errors.title}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Message *"
                                name="message"
                                value={newMeeting.message}
                                onChange={(e) => handleFieldChange('message', e.target.value)}
                                placeholder="Enter the message"
                                disabled={viewOpen}
                                multiline
                                error={!!errors.message}
                                helperText={errors.message}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonSelect
                                label="Recipient Type *"
                                name="category"
                                value={newMeeting.receipientType}
                                onChange={(e) => setNewMeeting({ ...newMeeting, receipientType: e.target.value, studentIds: [] })}
                                placeholder="Select Recipient Type"
                                options={ReceipientTypes}
                                disabled={viewOpen}
                            />
                        </Grid>
                        {newMeeting?.receipientType !== "ALL" && (
                            <Grid item sm={12}>
                                <Autocomplete
                                    value={users?.filter((c) => newMeeting?.studentIds?.includes(c?.student?.student_id)) || []}
                                    id="checkboxes-tags-demo"
                                    size="small"
                                    fullWidth
                                    multiple
                                    clearIcon={true}
                                    disableCloseOnSelect
                                    options={users}
                                    disabled={viewOpen}
                                    getOptionLabel={(option) => `${option?.first_name} ${option?.last_name !== null ? option?.last_name : ''}`}
                                    ListboxProps={{
                                        onScroll: (event) => loadNextUser(event),
                                    }}
                                    renderOption={(props, option, { selected }) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {`${option?.first_name} ${option?.last_name !== null ? option?.last_name : ''}`}
                                            </li>
                                        );
                                    }}
                                    onChange={(event, newValue) => {
                                        setNewMeeting((prev) => ({
                                            ...prev,
                                            studentIds: newValue.map((a) => a.student?.student_id)
                                        }));
                                        setUserPageNo(1);
                                    }}
                                    renderInput={(params) =>
                                        <TextField
                                            fullWidth
                                            {...params}
                                            label="Select Student"
                                            name="project"
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover fieldset': { border: '1px solid lightgray' },
                                                    '&.Mui-focused fieldset': { border: '1px solid lightgray' },
                                                    height: '40px',
                                                    '& input': { padding: '8px 12px', fontSize: '14px' },
                                                },
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />}
                                />
                            </Grid>
                        )}
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label="Priority"
                                name="priority"
                                value={newMeeting.priority}
                                onChange={(e) => setNewMeeting({ ...newMeeting, priority: e.target.value })}
                                placeholder="Select Priority"
                                options={TasksPriorityArr}
                                disabled={viewOpen}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonFormField
                                label="Start Date *"
                                type="date"
                                name="eventdate"
                                value={newMeeting.start_date}
                                onChange={(e) => handleFieldChange('start_date', e.target.value)}
                                placeholder="Start Date"
                                disabled={viewOpen}
                                error={!!errors.start_date}
                                helperText={errors.start_date}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomTimePicker
                                label="Start Time *"
                                value={newMeeting.start_time}
                                onChange={(e) => handleFieldChange('start_time', e.target.value)}
                                name="eventtime"
                                placeholder="Start Time"
                                format="24h"
                                disabled={viewOpen}
                                error={!!errors.start_time}
                                helperText={errors.start_time}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonFormField
                                label="Expiry Date"
                                type="date"
                                name="eventdate"
                                value={newMeeting.expiry_date}
                                onChange={(e) => handleFieldChange('expiry_date', e.target.value)}
                                placeholder="Expiry Date"
                                disabled={viewOpen}
                                error={!!errors.expiry_date}
                                helperText={errors.expiry_date}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomTimePicker
                                label="Expiry Time"
                                value={newMeeting.expiry_time}
                                onChange={(e) => handleFieldChange('expiry_time', e.target.value)}
                                name="eventtime"
                                placeholder="Expiry Time"
                                format="24h"
                                disabled={viewOpen}
                                error={!!errors.expiry_time}
                                helperText={errors.expiry_time}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        {!viewOpen && (
                            <CommonButton
                                label="Save"
                                size="small"
                                background={theme.palette.secondary.main}
                                color={theme.palette.background.main}
                                fontWeight={700}
                                marginBottom="10px"
                                disabled={isFormIncomplete || hasErrors}
                                handleClick={() => { handleSaveBlog(); }}
                            />
                        )}
                    </Fragment>
                )}
            />
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={600}>Manage Send Alerts</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                    <TextField
                        placeholder="Search By Alert Title"
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: theme.palette.background.main,
                                '&:hover fieldset': { border: '1px solid lightgray' },
                                '&.Mui-focused fieldset': { border: '1px solid lightgray' },
                            },
                            '& input': {
                                height: '24px',
                                fontSize: '16px',
                                fontWeight: 500,
                                width: '240px'
                            }
                        }}
                        onChange={(e) => { setSearchText(e.target.value); }}
                        value={searchText}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 30, color: theme.palette.background.dark }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <CommonButton
                        label={clearSearch ? "Clear" : "Search"}
                        marginBottom="0px"
                        width="120px"
                        fontWeight={600}
                        value={searchText}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => {
                            if (clearSearch) { setSearchText(''); }
                            setClearSearch(!clearSearch);
                            setPageNo(0);
                            setMeetings(null);
                        }}
                    />
                    <CommonButton
                        label="Add Alert"
                        marginBottom="0px"
                        width="140px"
                        fontWeight={600}
                        startIcon={<Add />}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => { setErrors({}); setAddOpen(true); }}
                    />
                </Box>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell>Alert Title</Cell>
                                <Cell>Recipient Type</Cell>
                                <Cell>Target Student Name</Cell>
                                <Cell>Priority</Cell>
                                <Cell>Status</Cell>
                                <Cell>Created Date</Cell>
                                <Cell>Expiry Date</Cell>
                                <Cell>Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {meetings?.data?.length > 0 ? meetings?.data?.map((blog) => (
                                <TableRow key={blog.meeting_id}>
                                    <InnerCell style={{ color: 'black' }}>{blog.title}</InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {ReceipientTypes.find((a) => a.value === blog.recipient_type)?.label}
                                    </InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {blog.alertRecipients?.map((a) => `${a?.student?.user?.first_name} ${a?.student?.user?.last_name ? a?.student?.user?.last_name : ''}`)?.join(', ')}
                                    </InnerCell>
                                    <InnerCell>{TasksPriorityArr.find((a) => a.value === blog.priority)?.label}</InnerCell>
                                    <InnerCell>{blog.status}</InnerCell>
                                    <InnerCell>{blog.start_at ? moment.utc(blog?.start_at)?.format('DD, MMM YYYY HH:mm A') : ''}</InnerCell>
                                    <InnerCell>{blog.expiry_at ? moment.utc(blog?.expiry_at)?.format('DD, MMM YYYY HH:mm A') : '-'}</InnerCell>
                                    <InnerCell>
                                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(blog)}>
                                            <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(blog)}>
                                            <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                handleOpenDialog(blog);
                                                setViewOpen(true);
                                            }}
                                        >
                                            <VisibilityOutlined sx={{ color: theme.palette.secondary.main }} />
                                        </IconButton>
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography>No Alert Found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                    component="div"
                    count={meetings?.pagination?.total || 0}
                    rowsPerPage={pageSize}
                    page={pageNo}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>
        </Fragment>
    );
};

export default ManageAlert;